import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Platform, TextInput } from 'react-native';
import {
  chatService,
  ChatMessageType,
  CallResult,
  ChatAction,
  CallConfirmationData,
  ChatResponseMessage,
  GetCallStatusResponse,
} from '@/services/chat.service';
import { useAudioStreamWebSocket } from '@/hooks/useAudioStreamWebSocket';
import { useChatWebSocket } from '@/hooks/useChatWebSocket';
import { getGlobalSessionId } from '@/services';

export interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  type: ChatMessageType;
  callingPhone?: string;
  callResult?: CallResult;
  refundAmount?: number;
  action?: ChatAction;
  callConfirmation?: CallConfirmationData;
  timestamp: Date;
  audioUrl?: string;
  audioDuration?: number;
}

interface UseChatOptions {
  initialSessionId?: string;
  onSessionCreated?: (sessionId: string | undefined) => void;
}

export function useChat(options: UseChatOptions = {}) {
  const { initialSessionId, onSessionCreated } = options;

  const [inputText, setInputText] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isPollingCall, setIsPollingCall] = useState(false);

  const inputTextRef = useRef(inputText);
  const inputRef = useRef<TextInput>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const callCompletedTimeRef = useRef<number | null>(null);
  const hasShownResultRef = useRef<boolean>(false);
  const startCallPollingRef = useRef<(phone: string) => void>(() => {});
  const processedMessageIds = useRef(new Set<string>());
  const isRestFallbackActive = useRef(false);

  // Get auth session ID for WebSocket connection
  const authSessionId = getGlobalSessionId();

  // Handle call status updates from WebSocket
  const handleCallStatusUpdate = useCallback((status: GetCallStatusResponse) => {
    console.log('[ChatWS] Call status update:', JSON.stringify({ status: status.status, audioUrl: status.audioUrl, result: status.result }));

    if (status.status === 'completed' || status.status === 'failed') {
      // Track when call first completed
      if (!callCompletedTimeRef.current) {
        callCompletedTimeRef.current = Date.now();
      }

      // For completed/failed calls, wait for recording URL before showing result
      const hasRecording = !!status.audioUrl;
      const RECORDING_WAIT_TIME = 15000;
      const waitTimeElapsed = callCompletedTimeRef.current && (Date.now() - callCompletedTimeRef.current > RECORDING_WAIT_TIME);

      // Continue waiting if no recording and within time limit
      if (!hasRecording && !waitTimeElapsed) {
        console.log('[ChatWS] Waiting for recording URL...');
        return;
      }

      // Stop polling and show result
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsPollingCall(false);

      // Only show result once
      if (hasShownResultRef.current) return;
      hasShownResultRef.current = true;

      // Add audio player for any call with recording
      if (status.audioUrl) {
        const audioMessage: ChatMessage = {
          id: `audio-${Date.now()}`,
          text: '',
          isUser: false,
          type: 'call_audio',
          audioUrl: status.audioUrl,
          audioDuration: status.audioDuration,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, audioMessage]);
      }

      // Add result text message
      const resultMessage: ChatMessage = {
        id: `result-${Date.now()}`,
        text: status.status === 'completed'
          ? status.refundAmount
            ? `Great news! Your refund of $${status.refundAmount} has been approved.`
            : 'Great news! Your refund request was successful.'
          : status.message || 'The call was not successful. Please try again later.',
        isUser: false,
        type: 'call_result',
        callResult: status.result,
        refundAmount: status.refundAmount,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, resultMessage]);
    }
  }, []);

  // Handle streaming chunks from WebSocket
  const handleChatResponseChunk = useCallback(
    (messageId: string, chunk: string, isComplete: boolean) => {
      // Always mark the messageId eagerly (even for empty final chunks) so that
      // handleChatResponse sees it as already-processed and merges metadata
      // instead of creating a second message
      if (!processedMessageIds.current.has(messageId)) {
        processedMessageIds.current.add(messageId);
      }

      if (chunk) {
        setMessages((prev) => {
          const existingIndex = prev.findIndex((m) => m.id === messageId);
          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex] = {
              ...updated[existingIndex],
              text: updated[existingIndex].text + chunk,
            };
            return updated;
          }
          // Defense-in-depth: even if the ref was somehow stale, check state
          if (prev.some((m) => m.id === messageId)) {
            return prev;
          }
          return [
            ...prev,
            {
              id: messageId,
              text: chunk,
              isUser: false,
              type: 'text' as ChatMessageType,
              timestamp: new Date(),
            },
          ];
        });
      }

      if (isComplete) {
        setIsLoading(false);
      }
    },
    [],
  );

  // Handle chat responses from WebSocket
  const handleChatResponse = useCallback((response: ChatResponseMessage, messageId: string) => {
    // If REST fallback is active, the REST handler will add the message.
    // Skip the WS broadcast to prevent REST/WS cross-delivery duplicate.
    if (isRestFallbackActive.current) {
      processedMessageIds.current.add(messageId);
      console.log('[Chat] Ignoring WS response during REST fallback:', messageId);
      return;
    }

    const alreadyProcessed = processedMessageIds.current.has(messageId);
    processedMessageIds.current.add(messageId);

    console.log('[ChatWS] Chat response:', response.type, messageId, alreadyProcessed ? '(streamed)' : '(direct)');

    setMessages((prev) => {
      const existingIndex = prev.findIndex((m) => m.id === messageId);
      if (existingIndex >= 0) {
        // Update the existing streamed message with full response data (metadata)
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          text: response.content,
          type: response.type,
          callingPhone: response.callingPhone,
          callResult: response.callResult,
          refundAmount: response.refundAmount,
          action: response.action,
          callConfirmation: response.callConfirmation,
        };
        return updated;
      }
      if (alreadyProcessed) {
        // Chunks already registered this ID but the message isn't in state yet
        // (React hasn't flushed the chunk updater). The chunk updater will run
        // before this one in the batched queue, so findIndex above should
        // normally catch it. If we still land here, skip to avoid duplicate.
        console.log('[Chat] Skipping duplicate response:', messageId);
        return prev;
      }
      // No streaming happened — add the full message
      return [
        ...prev,
        {
          id: messageId,
          text: response.content,
          isUser: false,
          type: response.type,
          callingPhone: response.callingPhone,
          callResult: response.callResult,
          refundAmount: response.refundAmount,
          action: response.action,
          callConfirmation: response.callConfirmation,
          timestamp: new Date(),
        },
      ];
    });

    setIsLoading(false);

    // If call is being initiated, start call tracking
    if (response.callingPhone) {
      startCallPollingRef.current(response.callingPhone);
    }

    // If session ended (call result received), reset for new session
    if (response.callResult) {
      setChatSessionId(null);
      onSessionCreated?.(undefined);
    }
  }, [onSessionCreated]);

  // Audio streaming WebSocket - connects when call is in progress
  const {
    isConnected: isAudioConnected,
    isPlaying: isAudioPlaying,
    disconnect: disconnectAudioWs,
  } = useAudioStreamWebSocket({
    sessionId: chatSessionId,
    enabled: isPollingCall && !!chatSessionId,
    onCallStatus: (status, message) => {
      console.log('[AudioStream] Call status:', status, message);
    },
    onConnectionChange: (connected) => {
      console.log('[AudioStream] Connection changed:', connected);
    },
  });

  // Chat WebSocket - connects when chat session is active
  const {
    isConnected: isChatWsConnected,
    isAITyping,
    sendMessage: sendWsMessage,
    disconnect: disconnectChatWs,
  } = useChatWebSocket({
    chatSessionId,
    authSessionId,
    enabled: !!chatSessionId && !!authSessionId,
    onChatResponse: handleChatResponse,
    onChatResponseChunk: handleChatResponseChunk,
    onCallStatus: handleCallStatusUpdate,
    onConnectionChange: (connected) => {
      console.log('[ChatWS] Connection changed:', connected);
    },
    onError: (error) => {
      console.error('[ChatWS] Error:', error);
    },
  });

  // Keep ref in sync with state
  useEffect(() => {
    inputTextRef.current = inputText;
  }, [inputText]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Initialize chat session
  const initializeSession = useCallback(async () => {
    if (chatSessionId) return;

    setIsInitializing(true);
    // Clear processed message IDs for new session
    processedMessageIds.current.clear();

    try {
      if (initialSessionId) {
        setChatSessionId(initialSessionId);
        const history = await chatService.getHistory(initialSessionId);
        const loadedMessages: ChatMessage[] = history.messages.map((msg, idx) => ({
          id: `history-${idx}`,
          text: msg.content,
          isUser: msg.role === 'user',
          type: msg.type,
          callingPhone: msg.callingPhone,
          callResult: msg.callResult,
          refundAmount: msg.refundAmount,
          action: msg.action,
          timestamp: new Date(),
        }));
        setMessages(loadedMessages);
      } else {
        setMessages([]);
        const response = await chatService.createSession();
        setChatSessionId(response.sessionId);
        onSessionCreated?.(response.sessionId);
      }
    } catch (error) {
      console.error('Failed to create chat session:', error);
      setMessages([
        {
          id: 'error-init',
          text: 'Failed to start chat. Please try again.',
          isUser: false,
          type: 'text',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsInitializing(false);
    }
  }, [chatSessionId, initialSessionId, onSessionCreated]);

  // Start call tracking - uses WebSocket for real-time updates, with polling fallback
  const startCallPolling = useCallback((_phone: string) => {
    if (!chatSessionId) return;

    // Reset tracking refs for new call
    callCompletedTimeRef.current = null;
    hasShownResultRef.current = false;

    setIsPollingCall(true);

    // If WebSocket is connected, call status updates will come via onCallStatus callback
    if (isChatWsConnected) {
      console.log('[CallTracking] Using WebSocket for call status updates');
      return;
    }

    console.log('[CallTracking] WebSocket not connected, falling back to polling');

    const pollStartTime = Date.now();
    const MAX_POLL_TIME = 120000; // 2 minutes timeout

    pollIntervalRef.current = setInterval(async () => {
      try {
        if (Date.now() - pollStartTime > MAX_POLL_TIME) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsPollingCall(false);

          const timeoutMessage: ChatMessage = {
            id: `timeout-${Date.now()}`,
            text: 'The call took too long. Please try again later.',
            isUser: false,
            type: 'call_result',
            callResult: 'failed',
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, timeoutMessage]);
          return;
        }

        const status = await chatService.getCallStatus(chatSessionId);
        console.log('[CallPolling] Call status:', JSON.stringify({ status: status.status, audioUrl: status.audioUrl, result: status.result }));
        handleCallStatusUpdate(status);
      } catch (error) {
        console.error('Failed to poll call status:', error);
      }
    }, 3000);
  }, [chatSessionId, isChatWsConnected, handleCallStatusUpdate]);

  // Keep startCallPolling ref in sync
  useEffect(() => {
    startCallPollingRef.current = startCallPolling;
  }, [startCallPolling]);

  // Send message
  const sendMessage = useCallback(async () => {
    const currentText = inputTextRef.current;
    if (!currentText.trim() || isLoading) return;

    const messageText = currentText.trim();

    // Clear input immediately
    setInputText('');
    inputTextRef.current = '';
    inputRef.current?.clear();
    if (Platform.OS !== 'web' && inputRef.current?.setNativeProps) {
      inputRef.current.setNativeProps({ text: '' });
    }

    // Check if session is ready
    if (!chatSessionId) {
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          text: 'Chat session not ready. Please wait a moment and try again.',
          isUser: false,
          type: 'text',
          timestamp: new Date(),
        },
      ]);
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: messageText,
      isUser: true,
      type: 'text',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    // Try to send via WebSocket first (sendWsMessage checks actual WS state
    // via ref, avoiding stale isChatWsConnected React state)
    if (sendWsMessage(messageText)) {
      console.log('[Chat] Message sent via WebSocket');
      return;
    }

    // Fallback to REST API — flag active so handleChatResponse ignores
    // any WS broadcast the server sends for the same response
    console.log('[Chat] WebSocket not connected, falling back to REST');
    isRestFallbackActive.current = true;
    try {
      const response = await chatService.sendMessage(chatSessionId, messageText);

      const aiMessage: ChatMessage = {
        id: `ai-${Date.now()}`,
        text: response.response.content,
        isUser: false,
        type: response.response.type,
        callingPhone: response.response.callingPhone,
        callResult: response.response.callResult,
        refundAmount: response.response.refundAmount,
        action: response.response.action,
        callConfirmation: response.response.callConfirmation,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);

      if (response.response.callingPhone) {
        startCallPolling(response.response.callingPhone);
      }

      if (response.response.callResult) {
        setChatSessionId(null);
        onSessionCreated?.(undefined);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          text: 'Failed to send message. Please try again.',
          isUser: false,
          type: 'text',
          timestamp: new Date(),
        },
      ]);
    } finally {
      isRestFallbackActive.current = false;
      setIsLoading(false);
    }
  }, [chatSessionId, isLoading, sendWsMessage, startCallPolling, onSessionCreated]);

  // Cleanup and reset chat state - call when exiting chat
  const cleanup = useCallback(() => {
    console.log('[Chat] Cleaning up chat resources');

    // Clear polling interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Disconnect WebSockets
    disconnectChatWs();
    disconnectAudioWs();

    // Reset state
    setInputText('');
    inputTextRef.current = '';
    setIsPollingCall(false);
    callCompletedTimeRef.current = null;
    hasShownResultRef.current = false;
    processedMessageIds.current.clear();
  }, [disconnectChatWs, disconnectAudioWs]);

  // Deduplicate messages by id as a safety net — last occurrence wins
  const deduplicatedMessages = useMemo(() => {
    const seen = new Set<string>();
    const result: ChatMessage[] = [];
    for (let i = messages.length - 1; i >= 0; i--) {
      if (!seen.has(messages[i].id)) {
        seen.add(messages[i].id);
        result.push(messages[i]);
      }
    }
    result.reverse();
    return result;
  }, [messages]);

  // Reset chat input only (for minor resets)
  const resetChat = useCallback(() => {
    setInputText('');
    inputTextRef.current = '';
  }, []);

  return {
    // State
    inputText,
    setInputText,
    messages: deduplicatedMessages,
    chatSessionId,
    isLoading,
    isInitializing,
    isPollingCall,
    isAITyping,
    isAudioConnected,
    isAudioPlaying,

    // Refs
    inputRef,

    // Actions
    initializeSession,
    sendMessage,
    resetChat,
    cleanup,
  };
}
